import * as React from "react";
import { useEffect, useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import QueryHelper from "components/common/QueryHelper";
import { Alert, DropdownButton, MenuItem } from "components/bootstrap";
import { PaginatedList, SearchForm } from "components/common";
import withPaginationQueryParameter from "components/common/withPaginationQueryParameter";
import AuditLogEntriesTable from "auditlog/AuditLogEntriesTable";
import { useAuditLogEntries, getAuditLogExportURL } from "auditlog/hooks/useAuditLog";
import style from "auditlog/AuditLogEntriesStyle.lazy.css";
const PAGE_SIZES = [20, 50, 100, 200, 500];
const EXAMPLE_QUERY_DATE = "timestamp:>=2017-03-23";
const EXAMPLE_QUERY_ACTIONS = "action:create action:delete";
const EXAMPLE_QUERY_ACTIONS_SHORT = "action:create,delete";
const EXPORT_FORMAT_JSON = "JSON";
const EXPORT_FORMAT_CSV = "CSV";
const QueryExamples = () => {
  const { t } = useTranslation("enterprise_auditlog");
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, t("entries.query_examples.after_date"), /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement("code", null, EXAMPLE_QUERY_DATE), /* @__PURE__ */ React.createElement("br", null)), /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(Trans, { t, i18nKey: "entries.query_examples.action", components: { code: /* @__PURE__ */ React.createElement("code", null) } }), /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement("code", null, EXAMPLE_QUERY_ACTIONS), /* @__PURE__ */ React.createElement("br", null), /* @__PURE__ */ React.createElement("code", null, EXAMPLE_QUERY_ACTIONS_SHORT)));
};
const StyledDropdownButton = styled(DropdownButton)`
  margin-left: 5;
`;
const AuditLogEntries = ({ paginationQueryParameter: { page, pageSize, resetPage } }) => {
  const { t } = useTranslation("enterprise_auditlog");
  const [query, setQuery] = useState("");
  const fieldMap = useMemo(
    () => ({
      actor: t("entries.fields.actor"),
      namespace: t("entries.fields.namespace"),
      object: t("entries.fields.object"),
      action: t("entries.fields.action"),
      success_status: t("entries.fields.success_status"),
      message: t("entries.fields.message"),
      timestamp: t("entries.fields.timestamp")
    }),
    [t]
  );
  useEffect(() => {
    style.use();
    return () => style.unuse();
  }, []);
  const { data: paginatedEntries } = useAuditLogEntries({ page, perPage: pageSize, query });
  const _onSearch = useCallback(
    (newQuery, cb) => {
      resetPage();
      setQuery(newQuery);
      cb();
    },
    [resetPage]
  );
  const _onReset = useCallback(() => {
    resetPage();
    setQuery("");
  }, [resetPage]);
  if (!paginatedEntries) {
    return /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, t("entries.loading"));
  }
  return /* @__PURE__ */ React.createElement("div", { className: "auditlog-entries" }, /* @__PURE__ */ React.createElement("h2", null, t("entries.title"), /* @__PURE__ */ React.createElement("span", null, "\xA0", /* @__PURE__ */ React.createElement("small", null, t("entries.total", { total: paginatedEntries.total })))), /* @__PURE__ */ React.createElement("div", { className: "auditlog-entries-content" }, /* @__PURE__ */ React.createElement(PaginatedList, { totalItems: paginatedEntries.total, pageSizes: PAGE_SIZES }, /* @__PURE__ */ React.createElement(
    SearchForm,
    {
      onSearch: _onSearch,
      onReset: _onReset,
      queryWidth: 500,
      useLoadingState: true,
      queryHelpComponent: /* @__PURE__ */ React.createElement(QueryHelper, { entityName: "audit event", example: /* @__PURE__ */ React.createElement(QueryExamples, null), commonFields: [], fieldMap })
    },
    /* @__PURE__ */ React.createElement(StyledDropdownButton, { id: "export-entries-dropdown", title: t("entries.export_results") }, /* @__PURE__ */ React.createElement(MenuItem, { href: getAuditLogExportURL("json", query), target: "_blank" }, EXPORT_FORMAT_JSON), /* @__PURE__ */ React.createElement(MenuItem, { href: getAuditLogExportURL("csv", query), target: "_blank" }, EXPORT_FORMAT_CSV))
  ), /* @__PURE__ */ React.createElement(AuditLogEntriesTable, { entries: paginatedEntries.entries }))));
};
export default withPaginationQueryParameter(AuditLogEntries, { pageSizes: PAGE_SIZES });
